import React, { FunctionComponent, useEffect, useState } from 'react';
import { QueryLoaderWrapperV2, ErrorWithHeader } from '@companion-professional/components';
import * as Sentry from '@sentry/browser';
import posthog from 'posthog-js';
import { useParams, useNavigate } from 'react-router-dom';
import { useCheckupRequest } from './hooks/useCheckupRequest';
import { CheckupWorkflowRouter } from './CheckupWorkflowRouter';
import { CheckupComplete } from './pages/CheckupComplete';
import { trackEvent } from './lib/trackEvent';
import { ConvertCheckupRequestV2ResponseToInfo } from '@companion-professional/dctypes';
import { CheckupUrlParams } from './hooks/useCheckupRequest';

// CheckupLoader loads the checkup based on the checkupId from the URL.  If the checkup is found, it will display the
// checkup routes.
export const CheckupLoader: FunctionComponent = () => {
  const { checkupId: urlCheckupId } = useParams<CheckupUrlParams>();
  const navigate = useNavigate();
  const [checkupId, setCheckupId] = useState<string | null>(null);

  // People sometimes copy/paste the URL from our SMS messages and/or clinics will sometimes add junk like a
  // sentence-ending period after the URL in a message they send. Either way, it's common to get a valid UUID followed
  // by some invalid junk. As a workaround, if we detect that we change the URL to be just the valid UUID prefix and
  // and some junk we parse our the UUID and navigate back to the page to start over. urlCheckupId is what we parse from
  // the URL and checkupId is the potentially cleaned up real checkup id.
  useEffect(() => {
    if (!urlCheckupId) {
      setCheckupId(null);
      return;
    }

    if (urlCheckupId.length > 36) {
      const potentialUuid = urlCheckupId.substring(0, 36);
      // UUID regex: 8-4-4-4-12 hex digits (case insensitive)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      if (uuidRegex.test(potentialUuid)) {
        // Replace the URL with the cleaned checkupId.
        navigate(`/${potentialUuid}`, { replace: true });
        return; // Don't set state, we're redirecting
      }
    }

    setCheckupId(urlCheckupId);
  }, [urlCheckupId, navigate]);

  const { isPending, error, checkupRequestv2Response, checkupComplete } = useCheckupRequest(checkupId); 

  useEffect(() => {
    if (!checkupRequestv2Response) {
      return;
    }

    // Once the checkup has been loaded, we can set the Sentry context to include the checkup information.
    Sentry.setContext('checkup', {
      checkup_id: checkupRequestv2Response.id,
      clinic_id: checkupRequestv2Response.clinic_id,
      pet_id: checkupRequestv2Response.pet_id
    });

    // Now that we have loaded the checkup, identify the user in PostHog.
    // TODO: the identification should really be the user id (i.e. the pet owner), but that part of the system is not
    //  currently set up, so for now we're using the pet_id as the unique identifier.  We should change this to the
    //  user id once we have that in place.  We are prefixing the pet id with "pet_" so it's clear that this is not a
    //  user id.
    posthog.identify(`pet_${checkupRequestv2Response.pet_id}`, {
      clinic_id: checkupRequestv2Response.clinic_id,
      clinic_name: checkupRequestv2Response.clinic_name,
      clinic_pet_id: checkupRequestv2Response.clinic_pet_id,
      pet_id: checkupRequestv2Response.pet_id,
      tags: checkupRequestv2Response.tags
    });

    // Configuring posthog so that all the capture events after this
    // have these metadata attached to them so that it's not duplicated.
    posthog.register({
      clinic_id: checkupRequestv2Response.clinic_id,
      clinic_name: checkupRequestv2Response.clinic_name,
      pet_id: checkupRequestv2Response.pet_id,
      checkup_id: checkupRequestv2Response.id,
    });

    trackEvent('checkup_loaded', {
      checkup_id: checkupRequestv2Response.id
    });
  }, [checkupRequestv2Response]);

  // Early returns after all hooks

  // Complete checkups should not be displayed
  if (!error && checkupRequestv2Response && checkupComplete) {
    // TODO: This is annoying, Ideally just move this method to the CheckupComplete component itself.
    // where you are just passing the checkupRequestv2Response directly and it does the transformation.
    const checkupRequest = ConvertCheckupRequestV2ResponseToInfo(checkupRequestv2Response);
    return <CheckupComplete message="A video has been uploaded for this checkup." checkupRequest={checkupRequest} />;
  }

  if (!isPending && !checkupRequestv2Response) {
    return (
        <ErrorWithHeader title="Checkup Not Found" message="We were not able to find a checkup with the provided ID." />
    );
  }

  return (
    <QueryLoaderWrapperV2 isPending={isPending} error={error} >
      {checkupRequestv2Response && !error ? <CheckupWorkflowRouter checkupRequestv2Response={checkupRequestv2Response} /> : null}
    </QueryLoaderWrapperV2>
  );
};
